import React from 'react';
import { useData } from '../../context/DataContext';
import Hero from '../../components/Hero/Hero';
import CategoryCards from '../../components/CategoryCards/CategoryCards';
import ImageShowcase from '../../components/ImageShowcase/ImageShowcase';
import ProductCard from '../../components/ProductCard/ProductCard';
import { getIconWithFallback } from '../../utils/iconMap';
import '../../styles/pages/Home.css';

const Home = () => {
  const { data, getProducts, getFeatures, getStatistics, getUIText, loading, error } = useData();
  const homeUI = getUIText('home');
  const products = getProducts();
  const features = getFeatures();
  const statistics = getStatistics();
  const ctaBackgroundImage = data?.images?.backgrounds?.cta;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>Error loading data: {error}</p>
      </div>
    );
  }

  const featuredProducts = products.filter(p => p.isFeatured).slice(0, 4);
  const displayProducts = featuredProducts.length > 0 ? featuredProducts : products.slice(0, 4);

  // Get image showcase data
  const imageShowcase = data?.imageShowcase;

  const ctaStyle = ctaBackgroundImage ? {
    '--cta-bg': `url(${ctaBackgroundImage})`
  } : {};

  return (
    <main className="home-page mandala-bg">
      <Hero />

      <CategoryCards />

      {/* Featured Products Section */}
      <section className="section products-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Products</h2>
            <p className="section-subtitle">
              {data?.products?.pageSubtitle || 'Fresh from the farm to your home'}
            </p>
          </div>
          <div className="product-grid">
            {displayProducts.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      {features.length > 0 && (
        <section className="section features-section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{homeUI?.featuresSection?.title || 'Why Choose Us?'}</h2>
              <p className="section-subtitle">{homeUI?.featuresSection?.subtitle}</p>
            </div>
            <div className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-card">
                  <div className="feature-icon">{getIconWithFallback(feature.icon)}</div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Showcase Section */}
      {imageShowcase?.enabled && imageShowcase?.images?.length > 0 && (
        <ImageShowcase
          images={imageShowcase.images}
          title={imageShowcase.title}
          subtitle={imageShowcase.subtitle}
          autoPlay={imageShowcase.autoPlay !== false}
          interval={imageShowcase.interval || 6000}
        />
      )}

      {/* Statistics Section */}
      {statistics.length > 0 && (
        <section className="section stats-section">
          <div className="container">
            <div className="stats-grid">
              {statistics.map((stat, index) => (
                <div key={index} className="stat-card">
                  <span className="stat-value">{stat.value}</span>
                  <span className="stat-title">{stat.title}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="section cta-section">
        <div className="container">
          <div className={`cta-content${ctaBackgroundImage ? ' has-bg-image' : ''}`} style={ctaStyle}>
            <h2 className="cta-title">{homeUI?.cta?.title || 'Embrace a Healthier, Natural Lifestyle'}</h2>
            <p className="cta-subtitle">{homeUI?.cta?.subtitle}</p>
            <a href="/contact" className="cta-button">Get Started</a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Home;
